import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AppLayout } from '@/components/layout';
import { ErrorBoundary } from '@/components/error-boundary';
import { ProtectedRoute } from '@/components/protected-route';
import { LoadingState } from '@/components/data-states';
import { AuthProvider } from '@/lib/auth-context';
import { ThemeProvider } from '@/lib/theme-context';
import { ApiError } from '@/lib/api';
import { lazyWithReload } from '@/lib/lazy-with-reload';
// Eager geladen: Login (Entry-Point) + Dashboard (Default-Route, häufigster Hit)
import { LoginPage } from '@/pages/login';
import { DashboardPage } from '@/pages/dashboard';
import { NotFoundPage } from '@/pages/not-found';

// Lazy-geladen: alle übrigen Pages. Reduziert Initial-Bundle deutlich,
// jede Page wird erst beim Navigieren nachgeladen. Vite erzeugt dafür
// automatisch eigene Chunks.
const CustomersListPage = lazyWithReload(() =>
  import('@/pages/customers/customers-list').then((m) => ({ default: m.CustomersListPage })),
);
const CustomerFormPage = lazyWithReload(() =>
  import('@/pages/customers/customer-form').then((m) => ({ default: m.CustomerFormPage })),
);
const ConsultantsListPage = lazyWithReload(() =>
  import('@/pages/consultants/consultants-list').then((m) => ({ default: m.ConsultantsListPage })),
);
const ConsultantFormPage = lazyWithReload(() =>
  import('@/pages/consultants/consultant-form').then((m) => ({ default: m.ConsultantFormPage })),
);
const ProjectsListPage = lazyWithReload(() =>
  import('@/pages/projects/projects-list').then((m) => ({ default: m.ProjectsListPage })),
);
const ProjectFormPage = lazyWithReload(() =>
  import('@/pages/projects/project-form').then((m) => ({ default: m.ProjectFormPage })),
);
const KontingentUebersichtPage = lazyWithReload(() =>
  import('@/pages/projects/kontingent-uebersicht-page').then((m) => ({
    default: m.KontingentUebersichtPage,
  })),
);
// BL-218: Rechnungen + Gutschriften teilen sich seit dem Tab-Umbau einen
// Chunk (OrderbuchTabsPage importiert beide List-Pages regulär) statt zwei
// getrennter Lazy-Chunks.
const OrderbuchTabsPage = lazyWithReload(() =>
  import('@/pages/orderbuch/orderbuch-tabs-page').then((m) => ({
    default: m.OrderbuchTabsPage,
  })),
);
const OrderbuchDetailPage = lazyWithReload(() =>
  import('@/pages/orderbuch/orderbuch-detail').then((m) => ({ default: m.OrderbuchDetailPage })),
);
const InvoiceNewPage = lazyWithReload(() =>
  import('@/pages/invoices/invoice-new').then((m) => ({ default: m.InvoiceNewPage })),
);
const DunningPage = lazyWithReload(() =>
  import('@/pages/dunning/dunning-page').then((m) => ({ default: m.DunningPage })),
);
const AuditLogPage = lazyWithReload(() =>
  import('@/pages/audit/audit-log').then((m) => ({ default: m.AuditLogPage })),
);
// BL-058 (18.05.2026): Compliance-Dashboard für die Betriebsprüfung.
const CompliancePage = lazyWithReload(() =>
  import('@/pages/compliance/compliance-page').then((m) => ({ default: m.CompliancePage })),
);
const BankAccountsPage = lazyWithReload(() =>
  import('@/pages/bank-accounts/bank-accounts-page').then((m) => ({ default: m.BankAccountsPage })),
);
const BillingPeriodsMatrixPage = lazyWithReload(() =>
  import('@/pages/billing-periods/billing-periods-matrix').then((m) => ({
    default: m.BillingPeriodsMatrixPage,
  })),
);
const ReportsPage = lazyWithReload(() =>
  import('@/pages/reports/reports-page').then((m) => ({ default: m.ReportsPage })),
);
const AusgabenPage = lazyWithReload(() =>
  import('@/pages/ausgaben/ausgaben-page').then((m) => ({ default: m.AusgabenPage })),
);
const LohnbuchhaltungPage = lazyWithReload(() =>
  import('@/pages/lohnbuchhaltung/lohnbuchhaltung-page').then((m) => ({
    default: m.LohnbuchhaltungPage,
  })),
);
const OposPage = lazyWithReload(() =>
  import('@/pages/opos/opos-page').then((m) => ({ default: m.OposPage })),
);
const UstvaPage = lazyWithReload(() =>
  import('@/pages/ustva/ustva-page').then((m) => ({ default: m.UstvaPage })),
);
const DatevExportPage = lazyWithReload(() =>
  import('@/pages/datev/datev-page').then((m) => ({ default: m.DatevExportPage })),
);
const UsersPage = lazyWithReload(() =>
  import('@/pages/users/users-page').then((m) => ({ default: m.UsersPage })),
);
const SettingsPage = lazyWithReload(() =>
  import('@/pages/settings/settings-page').then((m) => ({ default: m.SettingsPage })),
);
const AccountPage = lazyWithReload(() =>
  import('@/pages/account/account-page').then((m) => ({ default: m.AccountPage })),
);
const RolesPage = lazyWithReload(() =>
  import('@/pages/roles/roles-page').then((m) => ({ default: m.RolesPage })),
);
const PersonalcontrollingPage = lazyWithReload(() =>
  import('@/pages/personalcontrolling/personalcontrolling-page').then((m) => ({
    default: m.PersonalcontrollingPage,
  })),
);
const OAuthConsentPage = lazyWithReload(() =>
  import('@/pages/oauth/oauth-consent-page').then((m) => ({ default: m.OAuthConsentPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Performance-Audit P5 (05.05.2026): 5 Minuten staleTime statt 30s.
      // Vorher: jeder Tab-Wechsel nach 30s loeste auf Stammdaten-Pages
      // (customers, projects, billing-periods) neue Backend-Roundtrips aus,
      // obwohl die Daten sich praktisch nie aendern. Mutations invalidieren
      // ihre queryKeys ohnehin explizit (qc.invalidateQueries) — der
      // staleTime greift nur fuer "passive" Refresh-Trigger wie window-focus
      // oder navigieren-zur-Page. Realistisches Staleness-Risiko bei 2-3
      // gleichzeitigen Usern minimal; refetchOnWindowFocus bleibt aktiv und
      // greift sobald die 5 Min vorbei sind.
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        // 403/404 nicht retrien.
        // 401 → einmal retrien, weil das häufig eine kurze Session-Race
        // direkt nach Login oder Backend-Restart ist. Der 401-Handler in
        // api.ts macht einen eigenen /auth/me-Check, bevor er zum Login
        // umleitet — dieser Retry fängt transiente 401s für Widgets.
        if (error instanceof ApiError) {
          if ([403, 404].includes(error.status)) return false;
          if (error.status === 401) return failureCount < 1;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 3_000),
    },
  },
});

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            {/* BL-147: react-router v7 (Umstieg von v6.30) — die beiden
                Future-Flags v7_startTransition/v7_relativeSplatPath waren
                bereits in v6 aktiviert und sind in v7 fest eingebautes
                Verhalten, kein opt-in `future`-Prop mehr. */}
            <BrowserRouter>
              <Suspense fallback={<LoadingState label="Seite wird geladen …" />}>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />

                  {/* Standalone (kein AppLayout/Sidebar) — Consent-Zwischenschritt für
                      die MCP-OAuth-Anbindung, erreicht per Backend-Redirect von
                      GET /oauth/authorize. */}
                  <Route
                    path="/oauth/consent"
                    element={
                      <ProtectedRoute>
                        <OAuthConsentPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<DashboardPage />} />

                    <Route path="kunden" element={<CustomersListPage />} />
                    <Route path="kunden/neu" element={<CustomerFormPage />} />
                    <Route path="kunden/:nr" element={<CustomerFormPage />} />

                    <Route path="consultants" element={<ConsultantsListPage />} />
                    <Route path="consultants/neu" element={<ConsultantFormPage />} />
                    <Route path="consultants/:id" element={<ConsultantFormPage />} />

                    <Route path="projekte" element={<ProjectsListPage />} />
                    <Route path="projekte/neu" element={<ProjectFormPage />} />
                    <Route path="projekte/:id" element={<ProjectFormPage />} />
                    <Route path="kontingente" element={<KontingentUebersichtPage />} />
                    <Route path="rollen" element={<RolesPage />} />

                    <Route path="orderbuch" element={<OrderbuchTabsPage />} />
                    <Route path="orderbuch/:reNr" element={<OrderbuchDetailPage />} />
                    <Route path="rechnungen/neu" element={<InvoiceNewPage />} />
                    {/* BL-218 (26.08.2026): "Gutschriften" ist jetzt ein Tab unter
                        /orderbuch statt einer eigenen Route — alte Links/Bookmarks
                        bleiben über den Redirect erreichbar. */}
                    <Route
                      path="gutschriften"
                      element={<Navigate to="/orderbuch?tab=gutschriften" replace />}
                    />

                    <Route path="abrechnungszeitraeume" element={<BillingPeriodsMatrixPage />} />
                    <Route path="berichte" element={<ReportsPage />} />
                    <Route path="ausgaben" element={<AusgabenPage />} />
                    <Route path="lohnbuchhaltung" element={<LohnbuchhaltungPage />} />
                    <Route path="opos" element={<OposPage />} />
                    <Route path="ustva" element={<UstvaPage />} />
                    {/* BL-202 (26.08.2026): "Zahlungsabgleich" (CSV-Upload ohne
                        Persistenz/Dedupe) aus der UI geparkt (User-Entscheidung —
                        echtes Fehlzuordnungsrisiko: LOW-Match-Tier kann einen Zahlungs-
                        eingang auf eine andere, zufällig betragsgleiche offene Rechnung
                        matchen, die dann fälschlich als bezahlt gilt und aus dem
                        Mahnwesen verschwindet. /bankkonten deckt denselben Zweck bereits
                        sicherer ab — Persistenz, Dedupe seit BL-200, alreadyPaid-Schutz).
                        Backend-Service (payment-matching.ts) und Tests bleiben
                        unverändert — Reaktivierung ist ein einfaches Zurücknehmen dieser
                        Route + des Sidebar-Eintrags in components/layout.tsx, kein
                        Rückbau wie bei BL-017. */}
                    <Route path="bankkonten" element={<BankAccountsPage />} />
                    {/* BL-198 (25.08.2026): "Wiederkehrende Rechnungen" aus der UI
                        geparkt (User-Entscheidung — passt strukturell nicht zu avameos
                        tages-/stundenbasierter Abrechnung, 0 Definitionen in Prod).
                        Backend-Service, Prisma-Model, Scheduler und Tests bleiben
                        unverändert — Reaktivierung ist ein einfaches Zurücknehmen dieser
                        Route + des Sidebar-Eintrags in components/layout.tsx, kein
                        Rückbau wie bei BL-017. */}
                    <Route path="personalcontrolling" element={<PersonalcontrollingPage />} />
                    <Route path="datev" element={<DatevExportPage />} />
                    <Route path="einstellungen" element={<SettingsPage />} />
                    <Route path="konto" element={<AccountPage />} />
                    <Route path="benutzer" element={<UsersPage />} />
                    <Route path="mahnwesen" element={<DunningPage />} />
                    <Route path="protokoll" element={<AuditLogPage />} />
                    <Route path="compliance" element={<CompliancePage />} />

                    {/* BL-115: unbekannte Pfade INNERHALB des App-Shells zeigen eine
                        echte 404-Seite statt stumm aufs Dashboard umzuleiten — nur
                        eingeloggte User erreichen das (ProtectedRoute greift vorher). */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  {/* Fallback außerhalb des App-Shells (z.B. wenn ProtectedRoute selbst
                      nicht greift) — bleibt bewusst der stille Redirect. */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <Toaster
              position="top-right"
              richColors
              closeButton
              toastOptions={{
                className: 'fakt-toast',
                duration: 4000,
              }}
            />
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
